// Trends from Strava alone: the last four weeks against the four before. Distance a week, climb a
// week, moving speed, and heart rate for the work (km/h per beat), each with a direction. Shown
// only once the ramp has two weeks in it, so the first arrows aren't the summer against itself.
import type { Ride } from "./types";
import { addDays, daysBetween } from "./dates.ts";

export type Trend = { key: string; label: string; now: string; dir: "up" | "down" | "flat"; good: boolean | null; note: string };

const realR = (r: Ride) => r.km >= 15 && r.s > 0;

export function trends(rides: Ride[], today: string, rampStart: string): { ready: false; from: string } | { ready: true; items: Trend[] } {
  const from = addDays(rampStart, 14);
  if (daysBetween(rampStart, today) < 14) return { ready: false, from };
  const a0 = addDays(today, -27), b0 = addDays(today, -55), b1 = addDays(today, -28);
  const A = rides.filter((r) => realR(r) && r.d >= a0 && r.d <= today);
  const B = rides.filter((r) => realR(r) && r.d >= b0 && r.d <= b1);
  const sum = (xs: Ride[], f: (r: Ride) => number) => xs.reduce((t, r) => t + f(r), 0);
  const kmA = sum(A, (r) => r.km) / 4, kmB = sum(B, (r) => r.km) / 4;
  const mA = sum(A, (r) => r.m) / 4, mB = sum(B, (r) => r.m) / 4;
  const spd = (xs: Ride[]) => { const s = sum(xs, (r) => r.s); return s ? sum(xs, (r) => r.km) / (s / 3600) : 0; };
  const sA = spd(A), sB = spd(B);
  const eff = (xs: Ride[]) => { const h = xs.filter((r) => r.hr && r.hr > 60); if (h.length < 2) return 0; return sum(h, (r) => (r.km / (r.s / 3600)) / r.hr!) / h.length; };
  const eA = eff(A), eB = eff(B);
  const dir = (x: number, y: number, tol: number): Trend["dir"] => (!y ? "flat" : (x - y) / y > tol ? "up" : (y - x) / y > tol ? "down" : "flat");
  const pct = (x: number, y: number) => (y ? `${x >= y ? "+" : ""}${Math.round(((x - y) / y) * 100)}%` : "new");
  const items: Trend[] = [];
  const t = (key: string, label: string, now: string, x: number, y: number, tol: number, upGood: boolean | null) => {
    const d = dir(x, y, tol);
    items.push({ key, label, now, dir: d, good: d === "flat" || upGood == null ? null : (d === "up") === upGood, note: y ? `${pct(x, y)} on the four weeks before` : "Nothing in the four weeks before" });
  };
  t("km", "A week", `${Math.round(kmA)} km`, kmA, kmB, 0.08, true);
  t("climb", "Climb a week", `${Math.round(mA).toLocaleString("en-GB")} m`, mA, mB, 0.1, true);
  if (sA) t("speed", "Moving speed", `${sA.toFixed(1)} km/h`, sA, sB, 0.03, null);
  if (eA && eB) t("eff", "Speed per heartbeat", `${(eA * 100).toFixed(1)}`, eA, eB, 0.03, true);
  return { ready: true, items };
}
